/**
 * Pronto · useKitInProgress — iter v33 r5.
 *
 * User M1478: «Ma non è meglio che uno non usa l'AI così almeno non si impalla».
 * Quando un sopralluogo è in lavorazione (flag `pronto_in_creazione_v1` in
 * localStorage), TUTTI i bottoni che lanciano azioni AI pesanti devono essere
 * disabilitati per non rubare slot Nano Banana / LLM alla pipeline del kit.
 *
 * Cosa è considerato "AI azione pesante" (BLOCCATO):
 *   - Editor foto: Migliora, Arreda, Svuota stanza, Cambia arredo
 *   - Editor social: generazione card / reel
 *   - DescrizioneTab: "Genera descrizione" / "Rigenera"
 *   - Batch "Migliora tutte"
 *
 * Cosa NON è bloccato:
 *   - Sopralluogo nuovo (voice transcript Whisper è leggero, non interferisce)
 *   - Lettura/modifica testi manuali
 *   - Navigazione contatti/agenda/galleria
 *   - Download, share, copy link
 *
 * API:
 *   const { inProgress, info } = useKitInProgress();
 *   // inProgress: boolean
 *   // info: { pid, title, addr, startedAt } | null
 *
 *   // Helper per wrappare i click delle azioni AI:
 *   import { guardAIAction } from "@/hooks/useKitInProgress";
 *   const onClick = guardAIAction(realHandler, inProgress, "Migliora foto");
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "pronto_in_creazione_v1";

function readFlag() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function useKitInProgress() {
  const [info, setInfo] = useState(() => readFlag());

  useEffect(() => {
    const sync = () => setInfo(readFlag());
    window.addEventListener("storage", sync);
    window.addEventListener("pronto:in-creazione", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("pronto:in-creazione", sync);
    };
  }, []);

  const inProgress = !!info?.pid;

  // Helper: blocca l'esecuzione di una callback se kit attivo, mostra toast.
  const guard = useCallback(
    (fn, actionLabel = "Questa azione") => (...args) => {
      if (inProgress) {
        toast.warning("AI in pausa", {
          description: `${actionLabel} non disponibile · sto preparando il tuo sopralluogo (in media 4-6 min).`,
          duration: 4500,
        });
        return undefined;
      }
      return fn(...args);
    },
    [inProgress],
  );

  return { inProgress, info, guard };
}

/**
 * Versione standalone per casi in cui non puoi usare l'hook (es. dentro
 * `useCallback` esterni o componenti class). Legge la flag direttamente.
 */
export function guardAIAction(fn, actionLabel = "Questa azione") {
  return (...args) => {
    const flag = readFlag();
    if (flag?.pid) {
      toast.warning("AI in pausa", {
        description: `${actionLabel} non disponibile · sto preparando il tuo sopralluogo (in media 4-6 min).`,
        duration: 4500,
      });
      return undefined;
    }
    return fn(...args);
  };
}
